import axios from 'axios'
import Swal from 'sweetalert2'
import { ref, computed, watch, inject } from 'vue'
import Mango from './mango'
import { isEqual, cloneDeep } from 'lodash'
import { diff } from 'deep-diff'
import { useCheckoutStore } from '../stores/store.js'
import { captureCartError, CheckoutAction, ErrorLevel } from './sentry'
import { cartHasFulfilledOrder } from './orderStatus.js'
import {
	CART_STORAGE_KEYS,
	currentCartEpoch,
	isStaleCartEpoch,
	isAuthDeniedError,
	isGuestShopper,
	planStaleCartRecovery,
	selectInitialCart,
} from './cartRecovery.js'
import { cartSyncFailureDisposition, retryStatus0Network } from './networkError.js'

// Initialize store lazily when functions are called rather than at import time
const getCheckout = () => useCheckoutStore()

let couponWatcherEnabled = true

function dropLocalCart() {
	for (const key of CART_STORAGE_KEYS) {
		try {
			window.localStorage.removeItem(key)
		} catch {
			// storage can throw in private mode
		}
	}
}

let getLocalCart = async () => {
	const checkout = getCheckout()
	let localCart = checkout.getCartFromLocalStorage()
	if (!localCart) return null

	// Only a Mango cart id can be a member document the guest is not allowed to read.
	// A guest cart that only has a bcId must keep its current error behavior.
	const staleMemberCart = () => isGuestShopper() && !!localCart.id

	let completed = false
	try {
		completed = await cartHasFulfilledOrder(localCart)
	} catch (error) {
		if (isAuthDeniedError(error) && staleMemberCart()) {
			dropLocalCart()
			return null
		}
		throw error
	}

	try {
		let exists = await cartExists(localCart)
		if (!completed && !!exists) return localCart
		dropLocalCart()
	} catch (error) {
		if (isAuthDeniedError(error) && staleMemberCart()) {
			dropLocalCart()
			return null
		}
		throw error
	}
}

let getDbCart = async (user) => {
	if (isGuestShopper(user) && !window?.cart) return null
	try {
		let dbCart =
			window?.cart || user?.id
				? (await Mango.carts({ search: { author: user.id, completed: { $ne: true }, expired: { $ne: true } }, sort: { updated: -1 }, depthLimit: 2 }))?.[0]
				: null
		let completed = await cartHasFulfilledOrder(dbCart)
		let exists = await cartExists(dbCart)
		if (!completed && !!exists) return dbCart
		if (dbCart?.id) await Mango.carts.delete(dbCart.id)
	} catch (error) {
		// Permission failures must not delete the member cart.
		if (isAuthDeniedError(error) && isGuestShopper(user)) return null
		throw error
	}
}

// Check if the BC cart ID has already been converted to a fulfilled order
// If the cart has a mango ID, verify that ID exists in the database
let cartExists = async (cart) => {
	if (!cart?.id) return true
	let mangoCart = (await Mango.carts({ search: { id: cart?.id, completed: { $ne: true }, expired: { $ne: true } }, depthLimit: 2 }))?.[0]
	return !!mangoCart?.id
}

/**
 * Validates that a cart item has the minimum required fields to be usable
 * @param {Object} item - A cart item object
 * @returns {boolean} True if the item is valid
 */
let productRequiresVariant = (item) => {
	const product = typeof item?.item === 'object' ? item.item : null
	if (!product) return false
	return !!(product.optionSetId || product.inventoryTracking === 'variant' || product.variants?.length > 1)
}

let cartItemHasVariant = (item) => {
	if (!item?.variant) return false
	if (typeof item.variant === 'string') return !!item.variant
	return !!(item.variant?.id || item.variant?.bcId)
}

let isValidCartItem = (item) => {
	if (!item || typeof item !== 'object') return false
	// Must have an item reference (either populated object with id, or just the id string)
	const hasItem = item.item && (typeof item.item === 'string' || item.item?.id)
	const hasVariant = !productRequiresVariant(item) || cartItemHasVariant(item)
	// Must have a positive quantity
	const hasQuantity = typeof item.quantity === 'number' && item.quantity > 0
	return hasItem && hasVariant && hasQuantity
}

/**
 * Filters out null, undefined, or invalid cart items
 * @param {Array} cartItems - Array of cart items
 * @returns {Array} Sanitized array with only valid items
 */
let sanitizeCartItems = (cartItems) => {
	if (!Array.isArray(cartItems)) return []
	return cartItems.filter(isValidCartItem)
}

/**
 * Checks if a cart has any valid items (not just non-empty array)
 * @param {Object} cart - Cart object
 * @returns {boolean} True if cart has valid items
 */
let cartHasValidItems = (cart) => {
	const validCartItems = sanitizeCartItems(cart?.cartItems)
	const validCertificates = Array.isArray(cart?.certificates) ? cart.certificates.filter((c) => c && c.amount > 0) : []
	return validCartItems.length > 0 || validCertificates.length > 0
}

let simplifyCartItems = (cartItems) => {
	return cartItems.map((ci) => ({
		quantity: ci.quantity,
		item: ci.item?.id || ci.item,
		variant: ci.variant?.id || ci.variant,
		optional: ci?.optional?.coveredProductCode || null,
		queryID: ci?.queryID || null,
	}))
}

function authDeniedResult(error) {
	return {
		success: false,
		authDenied: true,
		data: null,
		warnings: error?.response?.data?.message || error?.message || 'Failed to sync cart',
		error,
	}
}

function failedCartSyncResult(error, warnings) {
	const disposition = cartSyncFailureDisposition(error)
	return {
		success: false,
		networkError: disposition.network,
		preserveCart: disposition.preserveCart,
		data: null,
		warnings: disposition.message || warnings || error?.message || 'Failed to sync cart',
		error,
	}
}

let processCart = async (cart, user, api, epoch) => {
	if (isStaleCartEpoch(epoch)) return { success: true, abandoned: true, data: null, warnings: null }
	let success, data, warnings, rawMangoResponse, oosProductBcId, oosProductName
	// No stored session means guest, even if this closure captured a member at login.
	let guest = isGuestShopper(user)
	let cartItems = cart.value?.cartItems?.map((ci) => ({
		quantity: ci.quantity,
		item: ci.item?.id || ci.item,
		variant: ci.variant?.id || ci.variant,
		optional: ci?.optional || null,
		...(ci?.optionSelections?.length > 0 && { optionSelections: ci.optionSelections }),
		queryID: ci?.queryID || null,
	}))
	let certificates = cart.value?.certificates

	let coupon = cart.value?.coupon || { code: '' }
	let couponDiscount = cart.value?.couponDiscount || 0
	let couponType = cart.value?.couponType || null
	let certificate = cart.value?.certificate || null
	let certificateDiscount = cart.value?.certificateDiscount || 0

	if (guest) {
		if (!cart.value?.cartItems?.length && !cart.value?.certificates?.length) return { success: true, data: null, warnings: null }

		let cartsUrl = `${api}/controllers/bigCommerce/sync/carts`
		const posted = await retryStatus0Network(() => axios.post(cartsUrl, cart.value), {
			shouldAbort: () => isStaleCartEpoch(epoch),
		})
		if (posted.aborted || isStaleCartEpoch(epoch)) return { success: true, abandoned: true, data: null, warnings: null }
		if (!posted.ok) {
			const error = posted.error
			if (isAuthDeniedError(error) && (cart.value?.id || cart.value?.bcId)) return authDeniedResult(error)
			captureCartError(error, {
				action: CheckoutAction.SYNC_CART,
				cart: cart.value,
				user: null,
				extra: {
					isGuest: true,
					endpoint: cartsUrl,
					errorMessage: error?.message,
					responseStatus: error?.response?.status,
					responseData: error?.response?.data,
					attempts: posted.attempts,
				},
			})
			return failedCartSyncResult(error, error?.response?.data?.message)
		}
		let response = posted.value

		if (response?.data?.authorized === false && (cart.value?.id || cart.value?.bcId)) {
			return authDeniedResult({ response: { status: response?.status, data: response.data } })
		}

		// console.log('guest syncCart response', response?.data)

		// console.log('response', response)

		warnings = response?.data?.message
		success = response?.data?.success
		data = response?.data?.data

		// Pass through OOS product info for guest carts (comes directly from createBCCart response)
		oosProductBcId = response?.data?.productBcId || null
		oosProductName = response?.data?.productName || null

		// console.log('processCart data', data)

		if (response?.data?.addCouponError) warnings = response.data.addCouponError

		// Store the full BC cart response for enrichment
		const bcCartResponse = data

		if (data?.coupons?.length) {
			// If a coupon is applied, "cart_amount" is replaced with "cart_amount_inc_tax" and "cart_amount_ex_tax"

			let coupon = data.coupons[0]

			// console.log('Checkout Coupon: ', coupon)

			data = {
				subTotal: data?.base_amount,
				total: data?.cart_amount_inc_tax,
				tax: data?.cart_amount_inc_tax - data?.cart_amount_ex_tax,
				bcId: data?.id,
				couponDiscount: coupon.discounted_amount,
				couponType: coupon.coupon_type,
				bcCartResponse, // Pass through for enrichment
			}
		} else {
			data = {
				subTotal: data?.base_amount,
				total: data?.cart_amount,
				tax: data?.cart_amount - data?.base_amount,
				bcId: data?.id,
				bcCartResponse, // Pass through for enrichment
			}
		}
	} else {
		let cartData = {
			cartItems,
			certificates,
			id: cart?.value?.id,
			coupon,
			couponDiscount,
			couponType,
			certificate,
			certificateDiscount,
			email: user?.email,
			firstName: user?.firstName,
			lastName: user?.lastName,
		}
		const saved = await retryStatus0Network(() => Mango.carts.save(cartData, false, true), {
			shouldAbort: () => isStaleCartEpoch(epoch),
		})
		if (saved.aborted || isStaleCartEpoch(epoch)) return { success: true, abandoned: true, data: null, warnings: null }
		let response
		if (!saved.ok) {
			const error = saved.error
			if (isAuthDeniedError(error) && (cart.value?.id || cart.value?.bcId || cartData.id)) return authDeniedResult(error)
			captureCartError(error, {
				action: CheckoutAction.SAVE_CART,
				cart: cart.value,
				user,
				extra: {
					isGuest: false,
					errorMessage: error?.message,
					cartDataId: cartData.id,
					attempts: saved.attempts,
				},
			})
			return { ...failedCartSyncResult(error, error?.message), rawMangoResponse: rawMangoResponse ? rawMangoResponse : null }
		}
		rawMangoResponse = saved.value
		response = rawMangoResponse

		if ((response?.authorized === false || response?.response?.authorized === false) && (cart.value?.id || cartData.id)) {
			return authDeniedResult({ response: { status: 401, data: response } })
		}

		data = response?.response
		warnings = response?.warnings
		success = !warnings

		// Log to Sentry if there are warnings (BC sync failed but Mango save succeeded)
		// Skip insufficient stock warnings - these are handled gracefully in saveCart
		const warningStr = Array.isArray(warnings) ? warnings.join(' ') : warnings || ''
		const isStockWarning = /insufficient stock|can only purchase a maximum/i.test(warningStr)
		const isKnownBcValidationWarning =
			/variant\s*id\s*is\s*required|modifier options|missing or incorrect required fields|missing required fields|product with the id of \d+ was not found/i.test(
				warningStr,
			)
		if (warnings && !isStockWarning && !isKnownBcValidationWarning) {
			captureCartError(warnings, {
				action: CheckoutAction.SYNC_CART,
				cart: cart.value,
				user,
				warnings,
				extra: {
					isGuest: false,
					mangoCartId: data?.id,
					bcCartId: data?.bcId,
					responseData: data,
				},
			})
		}
	}
	// console.log('Update cart success: ', success)
	// console.log('Update cart warnings: ', warnings)
	return { success, data, warnings, rawMangoResponse, oosProductBcId, oosProductName }
}

/**
 * Enriches cart items with pricing data from BigCommerce sync response
 * @param {Object} cart - The store cart object
 * @param {Object} bcCartResponse - The BigCommerce cart response data
 * @returns {Object} The enriched cart object
 */
export let enrichCartWithBCPricing = (cart, bcCartResponse) => {
	if (!bcCartResponse) return cart

	// Create a map of BC line items by product_id and variant_id for quick lookup
	const lineItemsMap = new Map()
	bcCartResponse.line_items?.physical_items?.forEach((item) => {
		const key = `${item.product_id}_${item.variant_id}`
		lineItemsMap.set(key, item)
	})

	// Enrich each cart item with pricing data from BC response
	const enrichedCartItems = cart.cartItems?.map((cartItem) => {
		const productId = cartItem.item?.bcId || cartItem.item?.id
		const variantId = cartItem.variant?.bcId || cartItem.variant?.id
		const key = `${productId}_${variantId}`
		const bcLineItem = lineItemsMap.get(key)

		if (bcLineItem) {
			return {
				...cartItem,
				price: {
					discountAmount: bcLineItem.discount_amount || 0,
					couponAmount: bcLineItem.coupon_amount || 0,
					originalPrice: bcLineItem.original_price || 0,
					listPrice: bcLineItem.list_price || 0,
					salePrice: bcLineItem.sale_price || 0,
					extendedListPrice: bcLineItem.extended_list_price || 0,
					extendedSalePrice: bcLineItem.extended_sale_price || 0,
				},
			}
		}

		return cartItem
	})

	// Add cart-level pricing fields from BC response
	const enrichedCart = {
		...cart,
		cartItems: enrichedCartItems,
		baseAmount: bcCartResponse.base_amount || 0,
		discountAmount: bcCartResponse.discount_amount || 0,
		taxIncluded: bcCartResponse.tax_included || false,
		manualDiscountAmount: bcCartResponse.manual_discount_amount || 0,
		cartAmountIncTax: bcCartResponse.cart_amount_inc_tax || bcCartResponse.cart_amount || 0,
		cartAmountExTax: bcCartResponse.cart_amount_ex_tax || bcCartResponse.cart_amount || 0,
	}

	return enrichedCart
}

const initCart = async (user, api) => {
	const epoch = currentCartEpoch()
	let localCart = await getLocalCart()
	let dbCart = await getDbCart(user)

	const savingCart = ref(false)

	// Use new validation that checks for VALID items, not just array length
	const localCartHasValidItems = cartHasValidItems(localCart)
	const dbCartHasValidItems = cartHasValidItems(dbCart)

	// Determine which cart source to use based on most recently modified
	let selectedCart = selectInitialCart({
		localCart,
		dbCart,
		localHasValidItems: localCartHasValidItems,
		dbHasValidItems: dbCartHasValidItems,
	})
	if (localCartHasValidItems && dbCartHasValidItems) {
		console.log(
			'[initCart] Both carts valid — localUpdated:',
			localCart?.updated,
			'dbUpdated:',
			dbCart?.updated,
			'selected:',
			selectedCart === dbCart ? 'dbCart' : 'localCart',
		)
	}

	// Sanitize cart items to remove any null/invalid entries that may have been corrupted
	if (selectedCart.cartItems?.length) {
		const originalItems = selectedCart.cartItems
		const originalCount = originalItems.length
		selectedCart.cartItems = sanitizeCartItems(originalItems)
		const removedCount = originalCount - selectedCart.cartItems.length
		if (removedCount > 0) {
			console.warn(`[Cart Init] Removed ${removedCount} invalid cart items (expected sanitization, not reported to Sentry)`)
		}
	}

	const cart = ref(selectedCart)

	const originalCart = ref(cloneDeep(cart.value))

	const itemsCount = computed(() => cart.value.cartItems?.reduce((acc, ci) => acc + (ci?.quantity || 0), 0))
	const certificatesCount = computed(() => cart.value?.certificates?.reduce?.((acc, cert) => acc + (cert?.quantity || 0), 0)) || 0
	const cartTotalItems = computed(() => itemsCount.value + certificatesCount.value)
	const cartItemsSubtotal = computed(() => cart.value.cartItems?.reduce((acc, ci) => acc + ci.quantity * (ci?.variant?.currentPrice || ci?.item?.currentPrice || 0), 0))
	const cartCertificatesSubtotal = computed(() => cart.value.certificates?.reduce((acc, cert) => acc + cert.quantity * (cert?.amount || 0), 0))
	const cartSubtotal = computed(() => cartItemsSubtotal.value + cartCertificatesSubtotal.value)
	const texasStateSalesTax = computed(() => cartSubtotal.value * 0.0825)
	const isDigitalOnly = computed(() => !cart.value.cartItems?.length && !!cart.value?.certificates?.length)

	// const cartTotal = computed(() => cartSubtotal.value + texasStateSalesTax.value)

	// Debugging statements
	// console.log('cartSubtotal:', cartSubtotal.value)
	// console.log('texasStateSalesTax:', texasStateSalesTax.value)

	const cartTotal = computed(() => {
		const subtotal = cartSubtotal.value
		const tax = texasStateSalesTax.value
		// console.log('subtotal:', subtotal)
		// console.log('tax:', tax)
		return subtotal + tax
	})
	//console.log('cartTotal:', cartTotal.value)

	const cartItems = computed(() => cart.value.cartItems)
	const certificates = computed(() => cart.value.certificates) // certificate(s) in the cart being purchased
	const coupon = computed(() => cart.value.coupon?.code || '')
	const certificate = computed(() => cart.value.certificate) // single certificate balance applied to cart total for a discount

	async function recoverStaleGuestCart(result) {
		const plan = planStaleCartRecovery({
			user,
			storage: window.localStorage,
			error: result?.error || { response: { status: 401, data: { authorized: false } } },
			cart: cart.value,
			originalCart: originalCart.value,
			attempt: 0,
		})
		if (!plan.recover) return result
		for (const key of plan.dropStorageKeys) {
			try {
				window.localStorage.removeItem(key)
			} catch {
				// storage can throw in private mode
			}
		}
		cart.value = plan.cart
		originalCart.value = cloneDeep(plan.cart)
		// One retry. The plan refuses a second recovery (attempt >= 1).
		return processCart(cart, user, api, epoch)
	}

	async function saveCart() {
		if (isStaleCartEpoch(epoch)) return
		console.log('saveCart triggered')
		const checkout = getCheckout()
		checkout.updateLoading(true)
		// console.log('checkout.loading: ', checkout.loading)
		// console.log('SAVE CART TRIGGERED!!')
		couponWatcherEnabled = false
		savingCart.value = true

		let processed = await processCart(cart, user, api, epoch)
		if (processed?.abandoned || isStaleCartEpoch(epoch)) {
			savingCart.value = false
			checkout.updateLoading(false)
			couponWatcherEnabled = true
			return
		}
		if (processed?.authDenied) processed = await recoverStaleGuestCart(processed)
		if (isStaleCartEpoch(epoch)) {
			savingCart.value = false
			checkout.updateLoading(false)
			couponWatcherEnabled = true
			return
		}
		let { success, data, warnings, rawMangoResponse, oosProductBcId, oosProductName, preserveCart } = processed

		// A status-0 sync never reached the server, so reverting would drop the edit
		// the shopper just made. Keep the live cart and stop; processCart already retried.
		if (preserveCart) {
			checkout.saveCartToLocalStorage(cart.value)
			originalCart.value = cloneDeep(cart.value)
			savingCart.value = false
			checkout.updateLoading(false)
			couponWatcherEnabled = true
			Swal.fire({
				icon: 'warning',
				title: 'Cart saved on this device',
				text: warnings || 'We could not reach the server to sync your cart. Your items are still on this device.',
				confirmButtonColor: '#e59789',
			})
			return
		}

		// If cart is empty and processCart returned success with null data, that's expected - not an error
		// This can happen on order confirmation page after checkout completes
		const cartIsEmpty = !cart.value?.cartItems?.length && !cart.value?.certificates?.length
		if (success && !data && cartIsEmpty && !warnings) {
			// Sync originalCart so re-adding the same items is detected as a change
			checkout.saveCartToLocalStorage(cart.value)
			originalCart.value = cloneDeep(cart.value)
			savingCart.value = false
			checkout.updateLoading(false)
			return
		}

		console.log('[saveCart] processCart result — success:', success, 'hasData:', !!data, 'warnings:', warnings, 'cartIsEmpty:', cartIsEmpty)

		if (warnings?.includes('Coupon') || warnings?.includes('coupon')) {
			// console.log('Removing coupon from cart...')
			if (data) data.coupon = { code: '' }
			Swal.fire('Invalid Coupon', `${warnings || 'An error occurred while applying this coupon to your cart.'}`, 'warning')
		} else if (warnings || !success || !data) {
			// Check for insufficient stock warnings FIRST - these are recoverable and don't need Sentry logging or page reload
			const warningList = Array.isArray(warnings) ? warnings : warnings ? [warnings] : []
			const hasInsufficientStock = warningList.some((warning) => warning?.includes('insufficient stock') || warning?.includes('can only purchase a maximum'))

			if (hasInsufficientStock) {
				console.log('Out of stock item in cart.')

				// Extract product info to auto-remove OOS items from the cart
				// For guest carts: oosProductBcId/oosProductName come from processCart return value
				// For logged-in users: the Mango collection hook embeds them as JSON in the warnings array
				const displayWarnings = []
				for (const warning of warningList) {
					try {
						const parsed = JSON.parse(warning)
						if (parsed?.productBcId) {
							if (!oosProductBcId) oosProductBcId = parsed.productBcId
							if (!oosProductName) oosProductName = parsed.productName
							continue
						}
					} catch (e) {
						// Not JSON - it's a regular warning string
					}
					displayWarnings.push(warning)
					// Fall back to name extraction from warning text
					if (!oosProductName) {
						const match = warning?.match(/^(.+?) has insufficient stock/)
						if (match) oosProductName = match[1]
					}
				}

				const cartBefore = cart.value.cartItems?.length || 0
				if (oosProductBcId) {
					// Remove by product bcId (most reliable)
					cart.value.cartItems = cart.value.cartItems.filter((ci) => ci.item?.bcId !== oosProductBcId)
					console.log(`Removed out-of-stock product (bcId: ${oosProductBcId}) from cart: ${oosProductName}`)
				} else if (oosProductName) {
					// Fall back to name matching (guest carts)
					cart.value.cartItems = cart.value.cartItems.filter((ci) => {
						const itemName = ci.item?.name || ci.product?.name || ''
						return itemName !== oosProductName
					})
					console.log(`Removed out-of-stock item by name: ${oosProductName}`)
				}

				const itemRemoved = cart.value.cartItems?.length < cartBefore
				if (!itemRemoved) {
					// Couldn't identify or remove the item - revert to originalCart
					if (cartHasValidItems(originalCart.value)) {
						cart.value = cloneDeep(originalCart.value)
					} else {
						cart.value = { cartItems: [], certificates: [], coupon: { code: '' }, couponDiscount: 0, couponType: false, certificate: null, certificateDiscount: 0 }
					}
					// Sync originalCart to match current cart so the watcher's diff check
					// finds no differences and doesn't re-trigger saveCart (prevents infinite Swal loop)
					originalCart.value = cloneDeep(cart.value)
					checkout.saveCartToLocalStorage(cart.value)
					savingCart.value = false
					checkout.updateLoading(false)
					Swal.fire('Insufficient Stock Available', `${displayWarnings.join(' ') || 'An error occurred while updating your cart.'}`, 'warning')
					return
				}

				// Item was removed - show Swal, then re-save so remaining items sync to BigCommerce
				checkout.saveCartToLocalStorage(cart.value)
				savingCart.value = false
				Swal.fire('Insufficient Stock Available', `<b>${oosProductName}</b> has insufficient stock and has been removed from your cart.`, 'warning')
				// Re-trigger saveCart for the remaining items (savingCart is false so the watcher will pick up the diff)
				debounceSave.value()
				return
			}

			// If success is true but data is null/undefined with a non-empty cart, this is unexpected
			// Log as warning level since it may be a transient BC sync issue
			const isTransientSyncIssue = success && !data && !warnings
			const errorLevel = isTransientSyncIssue ? ErrorLevel.WARNING : ErrorLevel.ERROR

			// For transient sync issues, log to Sentry but don't show error to user
			if (isTransientSyncIssue) {
				// Mango returned success:true with no data. The live in-memory cart is the
				// source of truth for what the user just added — never clear it here.
				// originalCart may legitimately be empty (e.g. first save of session or
				// after a previous failed sync); that is NOT a reason to wipe live items.
				const localCartValid = cartHasValidItems(cart.value)
				const originalCartValid = cartHasValidItems(originalCart.value)

				captureCartError('Transient Cart Sync: Mango returned success with null data', {
					action: CheckoutAction.SAVE_CART,
					cart: cart.value,
					user,
					level: ErrorLevel.WARNING,
					extra: {
						isTransientSyncIssue: true,
						success,
						hasData: false,
						localCartValid,
						originalCartValid,
						currentUrl: window.location.href,
						mangoRawResponse: JSON.stringify(rawMangoResponse, null, 2),
						vueCartSnapshot: JSON.stringify(cart.value, null, 2),
						vueCartItemsSnapshot: JSON.stringify(cart.value?.cartItems, null, 2),
						vueCertificatesSnapshot: JSON.stringify(cart.value?.certificates, null, 2),
						mangoCartId: cart.value?.id,
						bcCartId: cart.value?.bcId,
					},
				})

				// Preserve the live cart. Persist to localStorage so a refresh keeps the
				// user's items, and resync originalCart so the watcher's diff does not
				// re-trigger saveCart in a loop.
				checkout.saveCartToLocalStorage(cart.value)
				originalCart.value = cloneDeep(cart.value)

				savingCart.value = false
				checkout.updateLoading(false)
				couponWatcherEnabled = true
				return
			}

			const cartResetCount = checkout.getCartResetCount
			const newResetCount = cartResetCount + 1
			checkout.updateCartResetCount(newResetCount)

			// If cart has failed more than twice, stop the retry loop but preserve
			// the user's cart so they don't lose what they added. The bcId may be
			// stale, so detach it; the next save attempt will get a fresh one.
			if (newResetCount > 2) {
				captureCartError('Cart repeatedly failing - preserving local cart', {
					action: CheckoutAction.SAVE_CART,
					cart: cart.value,
					user,
					warnings,
					level: ErrorLevel.ERROR,
					extra: {
						cartResetCount: newResetCount,
						success,
						hasData: !!data,
						isTransientSyncIssue,
						originalCartId: originalCart.value?.id,
						currentUrl: window.location.href,
						vueCartSnapshot: JSON.stringify(cart.value, null, 2),
					},
				})

				// Persist locally so a refresh keeps the user's items.
				cart.value.bcId = null
				checkout.saveCartToLocalStorage(cart.value)
				originalCart.value = cloneDeep(cart.value)

				// Reset the count so the user can keep shopping; the next legitimate
				// change will retry the sync from a clean slate.
				checkout.updateCartResetCount(0)
				savingCart.value = false
				checkout.updateLoading(false)
				couponWatcherEnabled = true

				Swal.fire({
					icon: 'warning',
					title: 'Cart Sync Issue',
					text: 'We had trouble syncing your cart with our system. Your items have been saved on this device — please refresh the page or try again before checking out.',
					confirmButtonColor: '#e59789',
				})
				return
			}

			// Revert to previous cart state to keep the front-end in sync with
			// the BC cart (a failed save means BC reverted to its previous state).
			// An empty originalCart is valid — it means no items were successfully
			// synced yet, so the failed item should be removed from the UI.
			if (originalCart.value && Array.isArray(originalCart.value.cartItems)) {
				cart.value = cloneDeep(originalCart.value)
			} else {
				checkout.saveCartToLocalStorage(cart.value)
				originalCart.value = cloneDeep(cart.value)
			}
			originalCart.value = cloneDeep(cart.value)
			checkout.saveCartToLocalStorage(cart.value)
			savingCart.value = false
			checkout.updateLoading(false)
			couponWatcherEnabled = true

			// Log cart errors to Sentry
			captureCartError(warnings || 'An error occurred while updating your cart', {
				action: CheckoutAction.SAVE_CART,
				cart: cart.value,
				user,
				warnings,
				level: errorLevel,
				extra: {
					cartResetCount: newResetCount,
					success,
					hasData: !!data,
					isTransientSyncIssue,
					cartItemsCount: cart.value?.cartItems?.length,
					certificatesCount: cart.value?.certificates?.length,
					hasCoupon: !!cart.value?.coupon?.code,
					currentUrl: window.location.href,
					cartValue: JSON.stringify(cart.value, null, 2),
					cartItems: JSON.stringify(cart.value?.cartItems, null, 2),
				},
			})
			Swal.fire('Cart Error', `${warnings || 'An error occurred while updating your cart.'}`, 'warning')
			return
		}

		let mangoCart = data

		// console.log('mangoCart', mangoCart)

		if (!mangoCart) {
			console.warn('saveCart: mangoCart is null after processCart')
			// Log null mangoCart to Sentry
			captureCartError('saveCart: mangoCart is null after processCart', {
				action: CheckoutAction.SAVE_CART,
				cart: cart.value,
				user,
				extra: {
					success,
					hasWarnings: !!warnings,
					cartItemsCount: cart.value?.cartItems?.length,
				},
			})
			if (cartIsEmpty) {
				checkout.saveCartToLocalStorage(cart.value)
				originalCart.value = cloneDeep(cart.value)
			}
			savingCart.value = false
			checkout.updateLoading(false)
			return
		}

		// Enrich cart with BC pricing data
		if (mangoCart?.bcCartResponse?.id) {
			const enrichedCart = enrichCartWithBCPricing(cart.value, mangoCart.bcCartResponse)
			// Apply enriched cart items and cart-level pricing fields
			cart.value.cartItems = enrichedCart.cartItems
			cart.value.baseAmount = enrichedCart.baseAmount
			cart.value.discountAmount = enrichedCart.discountAmount
			cart.value.taxIncluded = enrichedCart.taxIncluded
			cart.value.manualDiscountAmount = enrichedCart.manualDiscountAmount
			cart.value.cartAmountIncTax = enrichedCart.cartAmountIncTax
			cart.value.cartAmountExTax = enrichedCart.cartAmountExTax
		}

		// Write back Mongo ID and totals to the Vue store cart:
		// cart.value.id = mangoCart?.id // don't writ back for now
		cart.value.tax = mangoCart?.tax || 0
		cart.value.subTotal = mangoCart?.subTotal || 0
		cart.value.total = mangoCart?.total || 0
		cart.value.bcId = mangoCart?.bcId

		cart.value.couponDiscount = mangoCart?.couponDiscount || 0
		cart.value.couponType = mangoCart?.couponType || null

		if (mangoCart?.id) cart.value.id = mangoCart.id

		if (!mangoCart?.couponType) {
			// Guest coupon is a different object from the mongo coupon object
			// coupon is added to the vue store cart when the code is applied (triggering the coupon watcher)
			// so only add the coupon if it doesn't exist in the cart already
			cart.value.coupon = mangoCart?.coupon || { code: '' }
		}

		checkout.saveCartToLocalStorage(cart.value)
		originalCart.value = cloneDeep(cart.value)
		savingCart.value = false
		couponWatcherEnabled = true

		// We use the same checkout instance from above
		checkout.updateLoading(false)
		// console.log('checkout.loading: ', checkout.loading)
	}

	let timer
	const debounceSave = computed(() => {
		return (...args) => {
			savingCart.value = true

			// Add things here that you want to be immediately invalidated on change
			cart.value.tax = null
			cart.value.total = null

			clearTimeout(timer)
			timer = setTimeout(() => {
				Promise.resolve(saveCart.apply(this, args)).catch((error) => {
					console.error('saveCart failed:', error)
					savingCart.value = false
					couponWatcherEnabled = true
					try {
						getCheckout().updateLoading(false)
					} catch {
						// checkout store can be unavailable during teardown
					}
				})
			}, 1200)
		}
	})

	watch(
		cartItems,
		(ci, oci) => {
			if (isStaleCartEpoch(epoch)) return
			const checkout = getCheckout()
			if (checkout?.validatingCart) return
			// Skip if a save is already in progress (prevents loops during error recovery)
			if (savingCart.value) return
			// Skip cart sync after successful checkout (cart was intentionally cleared)
			// But if cart has items again, user is shopping - clear the flag
			if (checkout?.checkoutSuccess) {
				if (ci?.length) {
					checkout.checkoutSuccess = false
				} else {
					return
				}
			}
			checkout.saveCartToLocalStorage(cart.value)

			// Check to see if anything actually changed:
			let newCi = simplifyCartItems(ci)
			let oldCi = simplifyCartItems(originalCart.value.cartItems)
			let differences = diff(newCi, oldCi)
			if (differences?.length) {
				checkout.bcOrderId = null
				debounceSave.value()
			}
			// if (user?.id) {
			// 	let newCi = simplifyCartItems(ci)
			// 	let oldCi = simplifyCartItems(originalCart.value.cartItems)
			// 	let differences = diff(newCi, oldCi)
			// 	// console.log('differences', differences, newCi, oldCi)
			// 	if (differences?.length) debounceSave.value()
			// } else {
			// 	debounceSave.value()
			// }
		},
		{ deep: true },
	)

	watch(
		certificates,
		(certs, oc) => {
			if (isStaleCartEpoch(epoch)) return
			const checkout = getCheckout()
			if (checkout?.validatingCart) return
			// Skip if a save is already in progress (prevents loops during error recovery)
			if (savingCart.value) return
			// Skip cart sync after successful checkout (cart was intentionally cleared)
			// But if cart has items again, user is shopping - clear the flag
			if (checkout?.checkoutSuccess) {
				if (certs?.length) {
					checkout.checkoutSuccess = false
				} else {
					return
				}
			}
			checkout.saveCartToLocalStorage(cart.value)
			// Compare against originalCart.certificates so a full cart.value
			// replacement (e.g. revert in saveCart) doesn't fire a spurious save.
			let differences = diff(certs || [], originalCart.value?.certificates || [])
			if (!differences?.length) return
			checkout.bcOrderId = null
			debounceSave.value()
		},
		{ deep: true },
	)

	watch(coupon, (nc, oc) => {
		if (isStaleCartEpoch(epoch)) return
		if (!couponWatcherEnabled) return
		const checkout = getCheckout()
		checkout.saveCartToLocalStorage(cart.value)
		// debounceSave.value()
	})

	watch(certificate, (nc, oc) => {
		if (isStaleCartEpoch(epoch)) return
		const checkout = getCheckout()
		checkout.saveCartToLocalStorage(cart.value)
		// debounceSave.value()
	})

	// Note: We no longer call saveCart() directly here. The validateInventory flow
	// (triggered by App.vue when cartInitialized becomes true) will handle validation
	// and saving. If the cart needs to be persisted to DB, the watcher will trigger
	// saveCart after validation completes and releases the guard.

	function syncOriginalCart() {
		const checkout = getCheckout()
		checkout.saveCartToLocalStorage(cart.value)
		originalCart.value = cloneDeep(cart.value)
	}

	return {
		cart,
		itemsCount,
		certificatesCount,
		cartTotalItems,
		cartSubtotal,
		savingCart,
		saveCart,
		syncOriginalCart,
		texasStateSalesTax,
		cartTotal,
		originalCart,
		isDigitalOnly,
	}
}

export default initCart
